/**
 * @file 課題の返しを録る hook。
 *
 * @what: マイクを1回だけ借りて録り、止めると音声を符号化した文字列を返す。録り終えたらマイクの
 *   取り込みを閉じる。
 * @why: 音声は送ったあとサーバで捨てられるので、画面でも溜めない。※なぜなら端末に残すと、
 *   保存しないという前提が画面の側で崩れるためである。
 * @failure: マイクを借りられない (許可されない・端末に無い) ときは `error` に1行を入れる。
 *   MediaRecorder を持たないブラウザでも同じ扱いにする。
 */

import { useCallback, useRef, useState } from "react";

/** 端末が出せる形の中から、サーバが受け取れるものを選ぶ。 */
const CANDIDATE_TYPES = ["audio/webm", "audio/mp4", "audio/ogg"] as const;

export type SpeakRecorderState = "idle" | "recording" | "encoding";

/** 録った音声を、送れる形にしたものである。 */
export interface SpeakRecording {
  /** 音声を符号化した文字列が入る。 */
  base64: string;
  /** サーバへ渡す種類が入る。 */
  mime: string;
  /** 録った長さ (秒)。画面に出して、長すぎる録音に気づけるようにする。 */
  seconds: number;
}

function pickType(): string | null {
  if (typeof MediaRecorder === "undefined") return null;
  for (const t of CANDIDATE_TYPES) {
    if (MediaRecorder.isTypeSupported(t)) return t;
  }
  return null;
}

/** Blob を符号化した文字列にする (data URL の頭を落とす)。 */
async function toBase64(blob: Blob): Promise<string> {
  const buf = await blob.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = "";
  // 一度に渡すと引数の数の上限に当たるので、小分けにして詰める。
  const step = 0x8000;
  for (let i = 0; i < bytes.length; i += step) {
    binary += String.fromCharCode(...bytes.subarray(i, i + step));
  }
  return btoa(binary);
}

export function useSpeakRecorder() {
  const [state, setState] = useState<SpeakRecorderState>("idle");
  const [error, setError] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);

  const start = useCallback(async (): Promise<boolean> => {
    setError(null);
    const mime = pickType();
    if (mime === null || !navigator.mediaDevices?.getUserMedia) {
      setError("このブラウザでは録音できません。別のブラウザでお試しください。");
      return false;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      console.warn("[study] microphone not available", err);
      setError("マイクを使えませんでした。ブラウザの許可をご確認ください。");
      return false;
    }
    const recorder = new MediaRecorder(stream, { mimeType: mime });
    chunksRef.current = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.start();
    recorderRef.current = recorder;
    startedAtRef.current = Date.now();
    setState("recording");
    return true;
  }, []);

  const stop = useCallback(async (): Promise<SpeakRecording | null> => {
    const recorder = recorderRef.current;
    if (recorder === null) return null;
    // すでに止まっていたら何もしない。※なぜなら止まっている MediaRecorder を止めると例外が
    // 投げられ、押し間違いや端末側の停止で画面が壊れるためである。
    if (recorder.state === "inactive") {
      for (const track of recorder.stream.getTracks()) track.stop();
      recorderRef.current = null;
      chunksRef.current = [];
      setState("idle");
      return null;
    }
    setState("encoding");
    const seconds = (Date.now() - startedAtRef.current) / 1000;
    const blob = await new Promise<Blob>((resolve) => {
      recorder.onstop = () => resolve(new Blob(chunksRef.current, { type: recorder.mimeType }));
      recorder.stop();
    });
    // マイクを開いたままにしない (録音の印が消えないと不安になる)。
    for (const track of recorder.stream.getTracks()) track.stop();
    recorderRef.current = null;
    chunksRef.current = [];
    try {
      const base64 = await toBase64(blob);
      setState("idle");
      // 種類はパラメータ (codecs=...) を落として渡す。サーバは種類だけを見る。
      return { base64, mime: recorder.mimeType.split(";")[0] ?? "audio/webm", seconds };
    } catch (err) {
      console.error("[study] failed to encode the recording", err);
      setError("録音を送れる形にできませんでした。もう一度お試しください。");
      setState("idle");
      return null;
    }
  }, []);

  /** 録音を捨てて止める (課題を閉じるときに使う)。 */
  const cancel = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder !== null) {
      for (const track of recorder.stream.getTracks()) track.stop();
      if (recorder.state !== "inactive") recorder.stop();
    }
    recorderRef.current = null;
    chunksRef.current = [];
    setState("idle");
  }, []);

  return { state, error, start, stop, cancel };
}
